import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'antd'

import PopupModal from 'Shared/Components/PopupModal'
import { Checkbox, Dropdown, FormContainer, PasswordInput, TextInput } from 'Shared/Components/FormComponents'
import useCommonAPI from 'Shared/API/Common'
import { showSuccess } from 'Shared/Components/NotifyToast'
import { useOutletContext } from 'react-router-dom'
import useSmartwatchAPI from 'Shared/API/Smartwatch'
import { apiUrl } from 'Shared/utils'
import useFetchData from 'Shared/Hooks/useFetchData'

const CameraFormContent = ({ camera, mode, onFinish }) => {  
  const { setPageLoading } = useOutletContext()
  
  const { t, i18n } = useTranslation()
  const { send } = useFetchData()

  const [form] = Form.useForm()
  const [projects, setProjects] = useState([])

  const loadProjects = async () => {
    const body = await send({
      method: "GET",
      url: `${apiUrl}/user/projects`,
      returnType: "json"
    })

    setProjects(body.map((i) => ({
      "value": i["project_code"],
      "label": i[`project_name_${i18n.language.toLowerCase()}`]
    })))
  }

  useEffect(() => {
    const init = async () => {
      setPageLoading(true)

      await loadProjects()

      setPageLoading(false)
    }

    init()
  }, [])

  const initialValues = mode != "Create" ? {
    cameraId: camera["camera_id"],
    cameraName: camera["name"],
    project:  camera["project"],
    model: camera["model"],
    sn: camera["sn"],
    ip: camera["ip"],
    http_port: camera["http_port"],
    ai_enabled: camera["ai_enabled"],
    usr: camera["usr"],
    pwd: ""
  } : {}

  return (
    <FormContainer form={form} onFinish={onFinish} initialValues={initialValues}>
      <TextInput field_name={t("cameras_deviceId")} field_id="cameraId" required={["Create"].includes(mode)} disabled={["Edit", "Delete"].includes(mode)}/>
      <TextInput field_name={t("general_deviceName")} field_id="cameraName" required={true} disabled={["Delete"].includes(mode)}/>
      <Dropdown field_name={t("general_project")} field_id="project" props={{"options": projects}} required={true} disabled={["Delete"].includes(mode)}/>
      <TextInput field_name={t("cameras_model")} field_id="model" disabled={["Delete"].includes(mode)}/>
      <TextInput field_name={t("cameras_serialNumber")} field_id="sn" disabled={["Delete"].includes(mode)}/>
      <TextInput field_name={t("cameras_ip")} field_id="ip" required={true} disabled={["Delete"].includes(mode)}/>
      <TextInput field_name={t("cameras_httpPort")} field_id="http_port" required={true} disabled={["Delete"].includes(mode)}/>
      <Checkbox field_name={t("cameras_aiEnabled")} field_id="ai_enabled" disabled={["Delete"].includes(mode)}/>

      {(mode != "Delete") && <>
        <TextInput field_name={t("cameras_username")} field_id="usr" required={true} disabled={["Delete"].includes(mode)}/>
        <PasswordInput field_name={mode == "Create" ? t("cameras_password") : t("cameras_changePassword")} field_id="pwd" required={["Create"].includes(mode)} disabled={["Delete"].includes(mode)}/>
      </>}
    </FormContainer>
  )
}

const CameraFormModal = ({ isOpen, camera, mode, onClose, loadData }) => {
  const { setPageLoading } = useOutletContext()

  const { t } = useTranslation()
  const { send } = useFetchData()

  const onFinish = async (values) => {    
    setPageLoading(true)

    if (mode == "Create") {
      const res = await send({
        method: "POST",
        url: `${apiUrl}/camera/device`,
        contentType: "json",
        body: {
          "camera_id": values["cameraId"],
          "camera_name": values["cameraName"],
          "project": values["project"],
          "model": values["model"],
          "sn": values["sn"],
          "ip": values["ip"],
          "http_port": values["http_port"],
          "ai_enabled": values["ai_enabled"],
          "usr": values["usr"],
          "pwd": values["pwd"],
        }
      })

      if (res != null) {
        onClose()
        showSuccess(t("general_success"))

        loadData()
      }
    }
    else if (mode == "Edit") {
      const res = await send({
        method: "PUT",
        url: `${apiUrl}/camera/device/${camera["camera_id"]}`,
        contentType: "json",
        body: {
          "camera_name": values["cameraName"],
          "project": values["project"],
          "model": values["model"],
          "sn": values["sn"],
          "ip": values["ip"],
          "http_port": values["http_port"],
          "ai_enabled": values["ai_enabled"],
          "usr": values["usr"],
          "pwd": values["pwd"],
        }
      })

      if (res != null) {
        onClose()
        showSuccess(t("general_success"))

        loadData()
      }
    }
    else if (mode == "Delete") {
      const res = await send({
        method: "DELETE",
        url: `${apiUrl}/camera/device/${camera["camera_id"]}`,
        contentType: "json"
      })

      if (res != null) {
        onClose()
        showSuccess(t("general_success"))

        loadData()
      }
    }

    setPageLoading(false)
  }

  return (
    <PopupModal isOpen={isOpen} close toggle={() => onClose()} width="80%" maxHeight="80%">
      <CameraFormContent camera={camera} mode={mode} onFinish={onFinish} />
    </PopupModal>
  )
}

export default CameraFormModal
